import React from 'react';
import { APP_CONFIG } from '../const/APP_CONFIG';

const Privacy = () => {
  return (
    <div
      style={{
        fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
        padding: '1em',
      }}
    >
      <strong>Privacy Policy</strong>
      <p>
        This privacy policy applies to the {APP_CONFIG.name} service (hereby referred to as
        "Application") created by Datium Corp. (hereby referred to as "Service
        Provider") as a Freemium service. It covers the {APP_CONFIG.name} mobile app, the{' '}
        {APP_CONFIG.name} web application at gethaist.com, and the {APP_CONFIG.name} browser
        extension (the "Extension") for use with auto-repair shop management
        platforms. This service is intended for use "AS IS".
      </p>
      <br />
      <strong>
        What information does the Application obtain and how is it used?
      </strong>
      <p>
        The Application may collect the following information when you use its features:
      </p>
      <ul>
        <li><strong>Account Information:</strong> Email address and name when you register or log in.</li>
        <li><strong>Authentication Information:</strong> Login tokens for your {APP_CONFIG.name} account and, when you use the Extension, the authentication token and shop identifier for your existing logged-in session on the supported shop management platform (Tekmetric). These tokens are used only to make authenticated requests to those services on your behalf so you do not have to log in a second time.</li>
        <li><strong>Repair Order &amp; Vehicle Data:</strong> When you use the Extension, vehicle and repair order information (such as year, make, model, VIN, mileage, customer concerns, inspection items, and repair order details) read from the shop management page you are viewing, so it can be imported into {APP_CONFIG.name} without manual re-entry.</li>
        <li><strong>Text Messages:</strong> Chat messages you send when using the AI-powered diagnosis feature.</li>
        <li><strong>Voice Recordings:</strong> Voice input when you use the voice conversation feature for vehicle diagnosis.</li>
        <li><strong>Photos &amp; Videos:</strong> Vehicle images and videos you capture or upload for AI-powered inspection and analysis.</li>
      </ul>
      <p>
        This information is used solely to provide the Application's core
        features: AI-powered vehicle diagnosis and inspection, importing repair
        order and vehicle data, and generating customer-facing inspection
        reports and estimates. We do not sell or transfer your data to third
        parties, and we do not use it for any purpose unrelated to these
        features (including determining creditworthiness or lending).
        Registration is not required to download the Application, but most
        features require an account.
      </p>
      <br />
      <strong>How is information from the browser Extension handled and stored?</strong>
      <p>
        The {APP_CONFIG.name} browser Extension provides a side panel that helps auto-repair
        technicians create and manage vehicle inspections while viewing a repair
        order on their shop management platform. To do this, the Extension:
      </p>
      <ul>
        <li>Reads vehicle and repair order data from the page you are actively viewing on the supported platform (Tekmetric) to auto-populate {APP_CONFIG.name}. This data is sent to the Service Provider's backend servers (hosted on Google Cloud) to create and update your inspection records.</li>
        <li>Captures your own session's authentication token and shop ID from the platform so the side panel can retrieve the repair order data on your behalf. These authentication values are stored in your browser's temporary session storage and are automatically cleared when you close the browser. They are never transmitted to any third party.</li>
        <li>Stores your {APP_CONFIG.name} login token and a small amount of non-sensitive configuration (such as inspection template identifiers) in your browser's local storage so you remain signed in across sessions.</li>
        <li>Uploads inspection photos and videos to cloud storage (the shop platform's Amazon Web Services storage and the Service Provider's Google Cloud Storage) using secure, time-limited upload links, so they can be attached to the inspection.</li>
      </ul>
      <p>
        The Extension only acts on the supported shop management platform and on
        the Service Provider's own domains. It does not track your browsing on
        other websites, and it does not collect your web history.
      </p>
      <br />
      <strong>
        Does the Application collect precise real time location information of
        the device?
      </strong>
      <p>
        This Application does not collect precise information about the location
        of your mobile device.
      </p>
      <br />
      <strong>
        Do third parties see and/or have access to information obtained by the
        Application?
      </strong>
      <p>
        When you use the AI-powered features (chat, voice conversation, or
        vehicle inspection), the data you provide (text messages, voice
        recordings, photos, and videos) is sent to <strong>Google Gemini</strong>,
        a third-party AI service operated by Google LLC, for real-time
        processing. This data is used solely to generate AI-powered diagnostic
        responses and is not stored by Google after the session ends.
      </p>
      <p>
        Google's handling of this data is governed by{' '}
        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
          Google's Privacy Policy
        </a>{' '}
        and{' '}
        <a href="https://ai.google.dev/gemini-api/terms" target="_blank" rel="noopener noreferrer">
          Gemini API Terms of Service
        </a>.
        Google provides equivalent or greater data protection measures for data
        processed through its API services.
      </p>
      <p>
        No other third parties have access to your personal data collected
        through the Application.
      </p>
      <br />
      <strong>AI-Powered Features and Data Processing</strong>
      <p>
        The Application provides AI-powered vehicle diagnosis and inspection
        services. When you use these features, the following data may be sent
        to Google Gemini (operated by Google LLC) for processing:
      </p>
      <ul>
        <li><strong>Text Messages:</strong> Chat messages for AI-assisted diagnosis.</li>
        <li><strong>Voice Recordings:</strong> Voice input for hands-free diagnosis conversations.</li>
        <li><strong>Photos &amp; Videos:</strong> Vehicle images and videos for AI-powered visual inspection.</li>
      </ul>
      <p>
        Before using AI features, you will be asked to provide explicit consent
        through an in-app consent dialog. You may revoke your consent at any
        time from the Application's Settings screen. If you revoke consent, AI
        features (chat, voice, and inspection) will be disabled until you agree
        again.
      </p>
      <p>
        Your data is processed in real-time by Google Gemini to generate
        diagnostic responses and is not retained by Google after the session
        ends. The Service Provider may store conversation history on its
        servers to allow you to access past diagnosis sessions.
      </p>
      <br />
      <strong>What are my opt-out rights?</strong>
      <p>
        You can revoke your AI data processing consent at any time from the
        Application's Settings screen. This will disable all AI-powered
        features. You can also stop all collection of information by
        uninstalling the Application. You may use the standard uninstall
        processes as may be available as part of your mobile device or via the
        mobile application marketplace or network.
      </p>
      <br />
      <strong>Children</strong>
      <p>
        The Application is not used to knowingly solicit data from or market to
        children under the age of 13.
      </p>
      <br />
      <p>
        The Service Provider does not knowingly collect personally identifiable
        information from children. The Service Provider encourages all children
        to never submit any personally identifiable information through the
        Application and/or Services. The Service Provider encourage parents and
        legal guardians to monitor their children's Internet usage and to help
        enforce this Policy by instructing their children never to provide
        personally identifiable information through the Application and/or
        Services without their permission. If you have reason to believe that a
        child has provided personally identifiable information to the Service
        Provider through the Application and/or Services, please contact the
        Service Provider ({APP_CONFIG.contact.email}) so that they will be able to take
        the necessary actions. You must also be at least 16 years of age to
        consent to the processing of your personally identifiable information in
        your country (in some countries we may allow your parent or guardian to
        do so on your behalf).
      </p>
      <br />
      <strong>Security</strong>
      <p>
        The Service Provider is concerned about safeguarding the confidentiality
        of your information. The Service Provider uses industry-standard
        encryption (TLS/SSL) to protect data transmitted between the
        Application and its servers, as well as data sent to third-party AI
        services. Access to your data is restricted to authorized personnel
        only.
      </p>
      <br />
      <strong>Your California Privacy Rights</strong>
      <p>
        If you are a California resident, you have the right to request access
        to the personal information we have collected about you, to request that
        we delete or correct it, and to opt out of any sale or sharing of your
        personal information. We do not sell or share your personal information,
        and we will not discriminate against you for exercising any of these
        rights. To make a request, contact us at {APP_CONFIG.contact.email}; we
        will verify and respond to your request as required by California law.
      </p>
      <br />
      <strong>Changes</strong>
      <p>
        This Privacy Policy may be updated from time to time for any reason. The
        Service Provider will notify you of any changes to their Privacy Policy
        by updating this page with the new Privacy Policy. You are advised to
        consult this Privacy Policy regularly for any changes, as continued use
        is deemed approval of all changes.
      </p>
      <br />
      <p>This privacy policy is effective as of 2026-06-05</p>
      <br />
      <strong>Your Consent</strong>
      <p>
        By using the Application, you are consenting to the processing of your
        information as set forth in this Privacy Policy now and as amended by
        the Service Provider.
      </p>
      <br />
      <strong>Contact Us</strong>
      <p>
        If you have any questions regarding privacy while using the Application,
        or have questions about the practices, please contact the Service
        Provider via email at {APP_CONFIG.contact.email}.
      </p>
    </div>
  );
};

export default Privacy;
